import { useParams } from 'react-router-dom';
import { PageContainer, ImageContainer, Title, Title2, MessageText, MissionsContainer, FlexContainer, InfoContainer, MissionsGrid, LoaderContainer } from './style-component';
import { TimeInfo } from '@/components/time-info/time-info';
import MissionStatus from '@/components/mission-status/mission-status';
import MissionStep from './mission-step/mission-step';
import useFetch from "@/hook/useFetch/useFetch";
import { useUserContext } from "@/contexts/user/useUserContext";
import { SpaceshipLoader } from "@/components/spaceship-loader/spaceship-loader";
import { getCampaign } from '@/services/campaign/campaign';
import { Mission } from '@/types/mission/mission-type';
import SEOHelmet from '@/components/SEOHelmet/SEOHelmet';
import { getMissionCustom } from '@/services/mission/missions';
import { Skeleton } from 'antd';
import { useMissionProgress } from '@/hook/useMissionProgress/useMissionProgress';
import { useEffect, useRef, useState, useMemo } from 'react';
import { amplitudeAddTrack, determineProjectName } from '@/utils/amplitud/event-track';
import { getTotalMissionsBasedOnEnv } from '@/utils/total-mission-based-on-env';

const CampaignPage: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const { user } = useUserContext();
  const { data: campaignData, loading: loadingCampaign } = useFetch(getCampaign, { id: campaignId });
  const { data: missions, loading: loadingMissions, startFetch: fetchMission } = useFetch<Mission[], object>(getMissionCustom,
    { campaign_id: campaignId, user_main_key: user?.main_key, order: 'start_date_asc' }, { autoFetch: true });
  const { progress, loading: loadingProgress } = useMissionProgress(campaignId ?? '', user?.main_key ?? '');
  const campaign = useMemo(() => campaignData?.[0] ?? {}, [campaignData]);
  const [totalCompleted, setTotalCompleted] = useState<boolean[]>([]);

  const totalMissions = useMemo(() => {
    if(progress?.total_missions) {
      return progress?.total_missions;
    }
    return missions?.length ?? 0;
  }, [progress, missions]);

  const firstRender = useRef<boolean>(true);
  useEffect(() => {
    if (user?.main_key && firstRender.current) {
      firstRender.current = false;
      fetchMission();
    }
  }, [user?.main_key, fetchMission]);

  const firstRenderAmplitude = useRef<boolean>(true);
  useEffect(() => {
    if (campaign?.name && firstRenderAmplitude.current) { 
      firstRenderAmplitude.current = false;
      amplitudeAddTrack(determineProjectName(`Campaña: ${campaign?.name}`), {
        title: determineProjectName(`Campaña: ${campaign?.name}`),
      })
    }
  }, [campaign]);

  if (loadingCampaign) {
    return (
      <LoaderContainer>
        <SpaceshipLoader height="150px" />
      </LoaderContainer>
    );
  }

  return (
    <PageContainer>
      <SEOHelmet
        metadata={{
          title: determineProjectName(`Campaña: ${campaign?.name}`),
          description: determineProjectName(`Campaña: ${campaign?.description}`)
        }}
      />
      <ImageContainer $imageUrl={campaign.banner_url} />
      <Title>{campaign.name}</Title>
      <InfoContainer>
        <MessageText>
          {campaign.description}
        </MessageText>

        <FlexContainer>
          {loadingProgress && <Skeleton paragraph={false} title={true} style={{ height: 30, width: 100 }} />}
          {!loadingProgress && (
            <MissionStep
              label='Misiones'
              totalCompleted={progress?.completed_missions ?? 0}
              totalMissions={getTotalMissionsBasedOnEnv(campaignId ?? '', totalMissions)} />
          )}
          <TimeInfo
            isCampaign={true}
            endDate={campaign.end_date}
            startDate={campaign.start_date}
          />
        </FlexContainer>
      </InfoContainer>

      <MissionsContainer>
        <Title2>Misiones</Title2>
        <MissionsGrid>
          {loadingMissions &&
            <center>
              <SpaceshipLoader height="150px" />
            </center>}
          {!loadingMissions && missions.map((mission: Mission, index: number) => (
            <MissionStatus
              key={mission?.id || index}
              mission={mission}
              onFunction={(_, completed) => {
                const newTotalCompleted = [...totalCompleted];
                newTotalCompleted[index] = completed;
                setTotalCompleted(newTotalCompleted);
              }}
            />
          ))}
        </MissionsGrid>
      </MissionsContainer>
    </PageContainer>
  );
};

export default CampaignPage;
